import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { useWebRouter } from "~utils/next-router/use-web-router";
import { useMemo } from "react";
import { graphierQLNextClient, sortAscendingByCreatedAt } from "utils";

import { useFilters, useSort } from "~maps-portal/states/filters";
import {
	GetMapsQuery,
	GetMapsQueryVariables,
} from "~maps-portal/types/__generated/gql/graphql";
import { dayjs } from "~utils/libs/dayjs";
import { useGetCurrentAccountCache } from "~auth/queries/useGetCurrentAccount";

const query = /* GraphQL */ `
	query GetMaps {
		getMaps {
			id
			createdAt
			updatedAt
			name
			description
		}
	}
`;

export type GetMapsResults = GetMapsQuery["getMaps"];
export type GetMapsVariables = GetMapsQueryVariables;

export const useGetMaps = (): UseQueryResult<GetMapsResults, Error> => {
	const router = useWebRouter();
	const { isLoading: isAccountLoading, data: account } =
		useGetCurrentAccountCache();

	return useQuery({
		enabled: !!router._query.accountName && !isAccountLoading,
		queryKey: ["getMaps"],
		queryFn: () =>
			graphierQLNextClient<GetMapsQuery, GetMapsVariables>({
				query,
				useKey: !account?._currentUserRole,
			}).then((res) => res.getMaps.sort(sortAscendingByCreatedAt)),
	});
};

export const useGetCurrentMaps = () => {
	const res = useGetMaps();
	const sort = useSort();
	const filters = useFilters();

	const sorted = useMemo(() => {
		if (!res.data) return [];

		return res.data.sort((a, b) =>
			sort === "asc"
				? dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix()
				: dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix(),
		);
	}, [res.data, sort]);

	const filtered = useMemo(() => {
		if (!filters.query) return sorted;
		return sorted.filter((data) =>
			data.name.toLowerCase().includes(filters.query!.toLowerCase()),
		);
	}, [sorted, filters.query]);

	return {
		...res,
		data: filtered,
	};
};
